import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { materialsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Package, 
  Pencil, 
  Trash2, 
  AlertTriangle,
  Filter
} from 'lucide-react';
import { HelpButton } from '../components/HelpButton';

const UNIT_TYPES = [
  { value: 'each', label: 'Each' },
  { value: 'yard', label: 'Yard' },
  { value: 'foot', label: 'Foot' },
  { value: 'inch', label: 'Inch' },
  { value: 'oz', label: 'Ounce (oz)' },
  { value: 'lb', label: 'Pound (lb)' },
  { value: 'gram', label: 'Gram' },
  { value: 'ml', label: 'Milliliter (ml)' },
  { value: 'liter', label: 'Liter' },
  { value: 'sqft', label: 'Square Foot' },
  { value: 'sheet', label: 'Sheet' },
  { value: 'roll', label: 'Roll' },
  { value: 'spool', label: 'Spool' },
];

export const Materials = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLowStock, setShowLowStock] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    notes: '',
    supplier: '',
    cost_per_unit: '',
    unit: 'each',
    current_stock: '',
    reorder_threshold: '',
    sku: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMaterials();
  }, [user, navigate, showLowStock]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = {};
      if (showLowStock) params.low_stock = true;
      const response = await materialsAPI.getAll(params);
      setMaterials(response.data);
    } catch (error) {
      toast.error('Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        name: material.name || '',
        notes: material.notes || '',
        supplier: material.supplier || '',
        cost_per_unit: material.cost_per_unit?.toString() || '',
        unit: material.unit || 'each',
        current_stock: material.current_stock?.toString() || '',
        reorder_threshold: material.reorder_threshold?.toString() || '',
        sku: material.sku || '',
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        name: '',
        notes: '',
        supplier: '',
        cost_per_unit: '',
        unit: 'each',
        current_stock: '',
        reorder_threshold: '',
        sku: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.cost_per_unit) {
      toast.error('Name and cost per unit are required');
      return;
    }

    const payload = {
      name: formData.name,
      notes: formData.notes || null,
      supplier: formData.supplier || null,
      cost_per_unit: parseFloat(formData.cost_per_unit),
      unit: formData.unit,
      current_stock: formData.current_stock ? parseFloat(formData.current_stock) : 0,
      reorder_threshold: formData.reorder_threshold ? parseFloat(formData.reorder_threshold) : null,
      sku: formData.sku || null,
    };

    try {
      if (editingMaterial) {
        await materialsAPI.update(editingMaterial.id, payload);
        toast.success('Material updated');
      } else {
        await materialsAPI.create(payload);
        toast.success('Material added');
      }
      setIsDialogOpen(false);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save material');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    
    try {
      await materialsAPI.delete(id);
      toast.success('Material deleted');
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  const filteredMaterials = materials.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.supplier?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isLowStock = (material) => {
    return material.reorder_threshold && material.current_stock <= material.reorder_threshold;
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FFFFFF] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-3xl font-bold text-[#1E3A5F] font-['Outfit']">Materials Inventory</h1>
              <p className="text-gray-600 mt-1">Track raw materials, costs, and stock levels</p>
            </div>
            <HelpButton tutorialId="materials" />
          </div>
          <Button 
            onClick={() => handleOpenDialog()} 
            className="btn-primary"
            data-testid="add-material-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Material
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 mb-6 shadow-hard">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search materials..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-materials"
              />
            </div>
            <Button
              variant={showLowStock ? "default" : "outline"}
              onClick={() => setShowLowStock(!showLowStock)}
              className={showLowStock ? "bg-amber-500 hover:bg-amber-600" : ""}
              data-testid="low-stock-filter"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Low Stock
            </Button>
          </div>
        </div>

        {/* Materials Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A5F]"></div>
          </div>
        ) : filteredMaterials.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-12 text-center shadow-hard">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No materials found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || showLowStock 
                ? "Try adjusting your filters" 
                : "Start by adding your first material"
              }
            </p>
            {!searchQuery && !showLowStock && (
              <Button onClick={() => handleOpenDialog()} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Material
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] overflow-hidden shadow-hard">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1E3A5F]">
                  <TableHead className="text-white font-semibold">Material</TableHead>
                  <TableHead className="text-white font-semibold">Supplier</TableHead>
                  <TableHead className="text-white font-semibold text-right">Cost/Unit</TableHead>
                  <TableHead className="text-white font-semibold text-right">Stock</TableHead>
                  <TableHead className="text-white font-semibold text-center">Status</TableHead>
                  <TableHead className="text-white font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMaterials.map((material) => (
                  <TableRow 
                    key={material.id} 
                    className="hover:bg-[#FFEDDF] transition-colors"
                    data-testid={`material-row-${material.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium text-[#1E3A5F]">{material.name}</p>
                        {material.notes && (
                          <p className="text-sm text-gray-500 truncate max-w-xs">{material.notes}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-700">{material.supplier || '-'}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${material.cost_per_unit.toFixed(2)}/{material.unit}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {material.current_stock} {material.unit}
                    </TableCell>
                    <TableCell className="text-center">
                      {isLowStock(material) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                          <AlertTriangle className="w-3 h-3" />
                          Low Stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                          In Stock
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(material)}
                          data-testid={`edit-material-${material.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(material.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-material-${material.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary Stats */}
        {materials.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Total Materials</p>
              <p className="text-2xl font-bold text-[#1E3A5F]">{materials.length}</p>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-amber-600">
                {materials.filter(isLowStock).length}
              </p>
            </div>
            <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-4 shadow-hard">
              <p className="text-sm text-gray-600">Total Inventory Value</p>
              <p className="text-2xl font-bold text-green-600">
                ${materials.reduce((sum, m) => sum + (m.cost_per_unit * m.current_stock), 0).toFixed(2)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg border-2 border-[#1E3A5F]">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F]">
              {editingMaterial ? 'Edit Material' : 'Add Material'}
            </DialogTitle>
            <DialogDescription>
              {editingMaterial 
                ? 'Update material information and stock levels' 
                : 'Add a new material to your inventory'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Material Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Sterling Silver Wire"
                  required
                  data-testid="material-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="cost_per_unit">Cost per Unit *</Label>
                <Input
                  id="cost_per_unit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_per_unit}
                  onChange={(e) => setFormData({...formData, cost_per_unit: e.target.value})}
                  placeholder="0.00"
                  required
                  data-testid="material-cost-input"
                />
              </div>
              
              <div>
                <Label htmlFor="unit">Unit Type *</Label>
                <Select
                  value={formData.unit}
                  onValueChange={(value) => setFormData({...formData, unit: value})}
                >
                  <SelectTrigger data-testid="material-unit-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_TYPES.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="current_stock">Current Stock</Label>
                <Input
                  id="current_stock"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.current_stock}
                  onChange={(e) => setFormData({...formData, current_stock: e.target.value})}
                  placeholder="0"
                  data-testid="material-stock-input"
                />
              </div>
              
              <div>
                <Label htmlFor="reorder_threshold">Reorder Threshold</Label>
                <Input
                  id="reorder_threshold"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.reorder_threshold}
                  onChange={(e) => setFormData({...formData, reorder_threshold: e.target.value})}
                  placeholder="Alert when below this"
                  data-testid="material-threshold-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="supplier">Supplier</Label>
                <Input
                  id="supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                  placeholder="e.g., Rio Grande"
                  data-testid="material-supplier-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="sku">SKU / Part Number</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) => setFormData({...formData, sku: e.target.value})}
                  placeholder="Optional SKU"
                  data-testid="material-sku-input"
                />
              </div>
              
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional notes about this material"
                  data-testid="material-notes-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-material-btn">
                {editingMaterial ? 'Update' : 'Add'} Material
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Materials;
